import { useState } from "react";

export const Button = ({
  children,
  onClick,
  fullWidth,
  variant = "contained",
  startIcon,
  endIcon,
  className,
  align = "center",
}) => {
  const [ripples, setRipples] = useState([]);

  const width = fullWidth ? "w-full" : "w-auto";

  const base = [
    "relative group inline-flex items-center",
    "rounded-lg px-4 py-2.5 text-sm font-light select-none",
    "transition-all duration-200 ease-out",
    "border cursor-pointer",
    "focus-visible:outline-none focus-visible:ring-2",
  ].join(" ");

  const variants = {
    contained: [
      "bg-transparent border-btnBorder text-mainText",
      "hover:bg-btnHover",
      "focus-visible:ring-inputFocus/60",
    ].join(" "),
    outlined: [
      "bg-transparent border-btnBorder text-mainText",
      "hover:bg-btnHover hover:border-velBlueSecond/40",
      "focus-visible:ring-inputFocus/60",
    ].join(" "),
    danger: [
      "bg-logoutColor/10 border-logoutColor/40 text-logoutColor",
      "hover:bg-logoutColor/20",
      "focus-visible:ring-logoutColor/60",
    ].join(" "),
    optimistic: [
      "bg-activeCase/10 border-activeCase/40 text-successButtonText",
      "hover:bg-activeCase/20",
      "focus-visible:ring-activeCase/60",
    ].join(" "),
    cautionary: [
      "bg-sunColor/10 border-sunColor/40 text-warningButtonText",
      "hover:bg-sunColor/20",
      "focus-visible:ring-sunColor/60",
    ].join(" "),
  };

  const variantStyle = variants[variant] || variants.contained;

  const alignMap = {
    start: "justify-start text-left",
    center: "justify-center text-center",
    end: "justify-end text-right",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  };

  const justify = alignMap[align] || alignMap.center;

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 1.4;
    const key = Date.now() + Math.random();

    setRipples((prev) => [...prev, { key, x, y, size }]);
    setTimeout(
      () => setRipples((prev) => prev.filter((r) => r.key !== key)),
      550,
    );

    onClick?.(e);
  };

  const rippleColors = {
    danger: "rgba(220,38,38,0.3)",
    optimistic: "rgba(34,197,94,0.3)",
    cautionary: "rgba(245,158,11,0.3)",
    contained: "rgba(14,165,233,0.3)",
    outlined: "rgba(14,165,233,0.2)",
  };

  const rippleColor = rippleColors[variant] || rippleColors.contained;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[width, base, variantStyle, className || ""].join(" ")}
    >
      <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg z-10">
        {ripples.map((r) => (
          <span
            key={r.key}
            className="absolute rounded-full animate-ripple"
            style={{
              left: r.x - r.size / 2,
              top: r.y - r.size / 2,
              width: r.size,
              height: r.size,
              backgroundColor: rippleColor,
            }}
          />
        ))}
      </span>

      <span
        className={`relative z-20 inline-flex w-full items-center gap-2 ${justify}`}
      >
        {startIcon && <span className="shrink-0">{startIcon}</span>}
        <span className="truncate">{children}</span>
        {endIcon && <span className="shrink-0">{endIcon}</span>}
      </span>

      <style>{`
        @keyframes ripple {
          0% { transform: scale(0); opacity: 0.8; }
          100% { transform: scale(1); opacity: 0; }
        }
        .animate-ripple {
          animation: ripple 550ms ease-out forwards;
        }
      `}</style>
    </button>
  );
};

export default Button;
